import React, { useState, useEffect, useCallback } from 'react';
import { FaExclamationTriangle, FaTimes, FaTrash, FaCheckCircle, FaSignOutAlt } from 'react-icons/fa';

/**
 * Global styled Confirm Modal — replaces all window.confirm() calls.
 * 
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: 'Delete item?', message: 'This cannot be undone.', type: 'danger' });
 *   if (ok) { ... }
 * 
 * Types: 'danger' (red), 'warning' (orange), 'info' (blue)
 */

let resolveRef = null;
const listeners = new Set();

// Imperative trigger — called from useConfirm hook
const openConfirm = (options) => {
  return new Promise((resolve) => {
    resolveRef = resolve;
    listeners.forEach(fn => fn({ open: true, ...options }));
  });
};

export const useConfirm = () => {
  return useCallback((options) => openConfirm(options), []);
};

const typeConfig = {
  danger:  { color: '#e74c3c', bg: 'rgba(231, 76, 60, 0.15)', Icon: FaTrash,              confirmLabel: 'Delete',  confirmStyle: { background: '#e74c3c' } },
  warning: { color: '#e67e22', bg: 'rgba(230, 126, 34, 0.15)', Icon: FaExclamationTriangle, confirmLabel: 'Confirm', confirmStyle: { background: '#e67e22' } },
  info:    { color: '#e67e22', bg: 'rgba(230, 126, 34, 0.15)',  Icon: FaCheckCircle,         confirmLabel: 'OK',      confirmStyle: { background: '#e67e22' } },
  logout:  { color: '#e74c3c', bg: 'rgba(231, 76, 60, 0.15)',  Icon: FaSignOutAlt,          confirmLabel: 'Confirm', confirmStyle: { background: '#e74c3c' } },
};

export default function ConfirmModal() {
  const [state, setState] = useState({ open: false, title: '', message: '', type: 'danger', confirmLabel: null });

  useEffect(() => {
    const handler = (options) => setState(options);
    listeners.add(handler);
    return () => listeners.delete(handler);
  }, []);

  const handleResponse = (confirmed) => {
    setState(s => ({ ...s, open: false }));
    if (resolveRef) {
      resolveRef(confirmed);
      resolveRef = null;
    }
  };

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape' && state.open) handleResponse(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [state.open]);

  if (!state.open) return null;

  const cfg = typeConfig[state.type] || typeConfig.danger;
  const confirmLabel = state.confirmLabel || cfg.confirmLabel;

  return (
    <div
      onClick={() => handleResponse(false)}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, padding: 20
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        style={{
          background: '#1a1a1a', borderRadius: 16, padding: '32px 36px', maxWidth: 420, width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid #333333',
          animation: 'slideIn 0.18s ease-out', fontFamily: 'Inter, sans-serif'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 24 }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%', background: cfg.bg, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <cfg.Icon style={{ fontSize: 20, color: cfg.color }} />
          </div>
          <div style={{ flex: 1 }}>
            <h3 id="confirm-title" style={{ margin: '0 0 8px', fontSize: 18, fontWeight: '700', color: '#ffffff' }}>{state.title}</h3>
            {state.message && <p style={{ margin: 0, fontSize: 14, color: '#95a5a6', lineHeight: 1.6 }}>{state.message}</p>}
          </div>
          <button onClick={() => handleResponse(false)} aria-label="Close"
            className="confirm-close-btn"
            style={{ 
              marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', 
              color: '#7f8c8d', fontSize: 18, padding: 0, flexShrink: 0, transition: 'color 0.2s'
            }}
          >
            <FaTimes />
          </button>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button onClick={() => handleResponse(false)}
            className="confirm-cancel-btn"
            style={{
              padding: '10px 24px', borderRadius: 8, border: '1px solid #444444',
              background: '#2c2c2c', color: '#ffffff', fontWeight: 600, fontSize: 14, cursor: 'pointer',
              transition: 'all 0.2s'
            }}>
            Cancel
          </button>
          <button onClick={() => handleResponse(true)} autoFocus
            className="confirm-ok-btn"
            style={{
              padding: '10px 24px', borderRadius: 8, border: 'none', color: '#ffffff',
              fontWeight: 600, fontSize: 14, cursor: 'pointer', ...cfg.confirmStyle,
              transition: 'all 0.2s'
            }}>
            {confirmLabel}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: scale(0.92) translateY(-10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }
        .confirm-close-btn:hover {
          color: #ffffff !important;
        }
        .confirm-cancel-btn:hover {
          background-color: #333333 !important;
          border-color: #555555 !important;
        }
        .confirm-ok-btn:hover {
          filter: brightness(1.1);
        }
      `}</style>
    </div>
  );
}
